import { QueryClient } from 'react-query';
import { queryKeys } from '../../../../hooks/queryKeyFactory';
import { WatchList } from '../../../../models/WatchList';
import { IssuerListDto, IssuerListItemDto } from '../../../../services/dtos/issuer/issuerList.dto';
import { fetchIssuers } from '../../../../services/issuers/issuers.service';
import { IssuersQueryParams } from '../../../../services/issuers/issuersQueryParams';
import { DATE_FORMATS, formatDate } from '../../../../utils/dateUtils';
import { WATCHLIST_TABLE_PAGE_SIZE } from '../WatchlistTable/WatchlistTable.constants';

export const ISSUERS_LIMIT_PER_WATCHLIST = 1500;
export const ISSUERS_LIMIT_PER_USER = 10000;

export const WATCHLIST_LIMIT_EXCEEDED_MESSAGE = `You have already reached ${ISSUERS_LIMIT_PER_WATCHLIST} issuers. You can remove existing issuers to add new ones`;
export const USER_LIMIT_EXCEEDED_MESSAGE = `You have already reached ${ISSUERS_LIMIT_PER_USER} issuers across all of your watchlists. You can remove existing issuers to add new ones`;
export const BOTH_LIMITS_EXCEEDED_MESSAGE = `You have already reached ${ISSUERS_LIMIT_PER_WATCHLIST} issuers allowed in one watchlist and ${ISSUERS_LIMIT_PER_USER} across all of your watchlists. You can remove existing issuers to add new ones`;
export const WATCHLIST_LIMIT_EXCEEDED_MESSAGE_COMPANY_PAGE = `Limit of ${ISSUERS_LIMIT_PER_WATCHLIST} issuers reached for this watchlist.`;
export const WATCHLIST_LIMIT_WOULD_EXCEED_MESSAGE_COMPANY_PAGE = `Limit of ${ISSUERS_LIMIT_PER_WATCHLIST} issuers would be reached for this watchlist. You can remove existing issuers to add new ones.`;
export const USER_LIMIT_EXCEEDED_MESSAGE_COMPANY_PAGE = `Limit of ${ISSUERS_LIMIT_PER_USER} issuers reached across all watchlists.`;

export const getIsWatchlistLimitExceeded = (watchlist?: WatchList, selectedCompaniesLength?: number): boolean => {
  const selectedCompaniesCount = selectedCompaniesLength || 0;
  const watchlistCompaniesCount = watchlist?.tags.length || 0;

  if (selectedCompaniesCount > 0) {
    return watchlistCompaniesCount + selectedCompaniesCount > ISSUERS_LIMIT_PER_WATCHLIST;
  }
  return watchlistCompaniesCount >= ISSUERS_LIMIT_PER_WATCHLIST;
};

export const getTotalTagsCount = (watchlists?: WatchList[]): number =>
  watchlists?.map((watchlist) => watchlist.tags.length)?.reduce((acc, value) => acc + value, 0) ?? 0;

export const getIsUserLimitExceeded = (watchlists?: WatchList[]): boolean =>
  getTotalTagsCount(watchlists) >= ISSUERS_LIMIT_PER_USER;
export const createExportFileName = (): string => `Watchlist_${formatDate(new Date(), DATE_FORMATS.YEAR_MONTH_DAY)}`;

export const fetchAllPagesForExport = async (
  queryClient: QueryClient,
  totalResults: number,
  queryParams: IssuersQueryParams
): Promise<IssuerListDto[]> => {
  const numberOfFetches = Math.ceil(totalResults / WATCHLIST_TABLE_PAGE_SIZE);
  const fetches: Promise<IssuerListDto>[] = [];
  for (let i = 0; i < numberOfFetches; i += 1) {
    const payload = {
      ...queryParams,
      skip: WATCHLIST_TABLE_PAGE_SIZE * i,
    };
    const query = queryClient.fetchQuery<IssuerListDto, Error>(queryKeys.fetchIssuers.query(payload), () =>
      fetchIssuers(payload)
    );
    fetches.push(query);
  }
  return Promise.all(fetches);
};

export const mapExtraPagesForExport = (issuerList: IssuerListDto): IssuerListItemDto[] =>
  issuerList.results.map((issuer) => ({ ...issuer, hidden: true }));
